// Feature Flag Service
// Centralized feature visibility control for Broker portal
// Managed exclusively by Lender Admin

// Feature flag storage key
const FEATURE_FLAGS_KEY = 'uff_broker_feature_flags';

// Default feature flags - all features disabled by default for staged rollout
const DEFAULT_FEATURE_FLAGS = {
  // Navigation Features
  scenarioPricer: false,
  dashboard: false,
  pipeline: false,
  users: false,
  offices: false,
  compensation: false,
  
  // Loan Detail Tabs
  loanOverview: true, // Always enabled - core functionality
  loanBorrower: true, // Always enabled - core functionality
  loanProperty: true, // Always enabled - core functionality
  loanDetails: true, // Always enabled - core functionality
  loanDocuments: false,
  loanConditions: false,
  loanActivity: false,
  loanPricing: false,
  loanFees: false,
  loanAUS: false,
  loanLocking: false,
  loanChat: false,
  loanCOC: false,
  
  // Action Buttons
  newLoanButton: false,
  pullCreditButton: false,
  orderAppraisalButton: false,
  openTitleButton: false,
  orderVOAButton: false,
  orderVOIEButton: false,
  runPricingButton: false,
  runAUSButton: false,
  pullFeesButton: false,
  
  // Advanced Features
  aiAssistant: false,
  bulkActions: false,
  advancedReporting: false,
};

// Feature metadata for display in Lender Admin
export const FEATURE_METADATA = {
  // Navigation Features
  scenarioPricer: {
    name: 'Scenario Pricer',
    description: 'Loan scenario pricing calculator',
    category: 'Navigation',
    icon: 'ti-calculator'
  },
  dashboard: {
    name: 'Dashboard',
    description: 'Broker admin dashboard with analytics',
    category: 'Navigation',
    icon: 'ti-layout-dashboard'
  },
  pipeline: {
    name: 'Pipeline',
    description: 'Loan officer pipeline view',
    category: 'Navigation',
    icon: 'ti-list'
  },
  users: {
    name: 'Users Management',
    description: 'Manage broker users and loan officers',
    category: 'Navigation',
    icon: 'ti-users'
  },
  offices: {
    name: 'Offices Management',
    description: 'Manage broker office locations',
    category: 'Navigation',
    icon: 'ti-building'
  },
  compensation: {
    name: 'Compensation',
    description: 'View and manage compensation settings',
    category: 'Navigation',
    icon: 'ti-currency-dollar'
  },
  
  // Loan Detail Tabs
  loanOverview: {
    name: 'Overview Tab',
    description: 'Loan overview and key metrics',
    category: 'Loan Details',
    icon: 'ti-layout-dashboard',
    alwaysEnabled: true
  },
  loanBorrower: {
    name: 'Borrower Tab',
    description: 'Borrower information and details',
    category: 'Loan Details',
    icon: 'ti-user',
    alwaysEnabled: true
  },
  loanProperty: {
    name: 'Property Tab',
    description: 'Property information and details',
    category: 'Loan Details',
    icon: 'ti-home',
    alwaysEnabled: true
  },
  loanDetails: {
    name: 'Loan Details Tab',
    description: 'Loan terms and financial details',
    category: 'Loan Details',
    icon: 'ti-file-text',
    alwaysEnabled: true
  },
  loanDocuments: {
    name: 'Documents Tab',
    description: 'Document upload and management',
    category: 'Loan Details',
    icon: 'ti-files'
  },
  loanConditions: {
    name: 'Conditions Tab',
    description: 'Underwriting conditions tracking',
    category: 'Loan Details',
    icon: 'ti-checklist'
  },
  loanActivity: {
    name: 'Activity Tab',
    description: 'Loan activity timeline and history',
    category: 'Loan Details',
    icon: 'ti-history'
  },
  loanPricing: {
    name: 'Pricing Tab',
    description: 'Rate pricing and scenarios',
    category: 'Loan Details',
    icon: 'ti-calculator'
  },
  loanFees: {
    name: 'Fees Tab',
    description: 'Fees worksheet and closing costs',
    category: 'Loan Details',
    icon: 'ti-receipt'
  },
  loanAUS: {
    name: 'AUS Tab',
    description: 'Automated underwriting system results',
    category: 'Loan Details',
    icon: 'ti-robot'
  },
  loanLocking: {
    name: 'Locking Tab',
    description: 'Rate lock management and history',
    category: 'Loan Details',
    icon: 'ti-lock'
  },
  loanChat: {
    name: 'Chat Tab',
    description: 'Internal loan chat and messaging',
    category: 'Loan Details',
    icon: 'ti-message'
  },
  loanCOC: {
    name: 'COC Tab',
    description: 'Change of Circumstance requests (TRID-compliant)',
    category: 'Loan Details',
    icon: 'ti-file-alert'
  },
  
  // Action Buttons
  newLoanButton: {
    name: 'New Loan Button',
    description: 'Create new loan application',
    category: 'Actions',
    icon: 'ti-plus'
  },
  pullCreditButton: {
    name: 'Pull Credit',
    description: 'Order credit reports',
    category: 'Actions',
    icon: 'ti-credit-card'
  },
  orderAppraisalButton: {
    name: 'Order Appraisal',
    description: 'Order property appraisals',
    category: 'Actions',
    icon: 'ti-home-dollar'
  },
  openTitleButton: {
    name: 'Open Title',
    description: 'Open title orders',
    category: 'Actions',
    icon: 'ti-file-certificate'
  },
  orderVOAButton: {
    name: 'Order VOA',
    description: 'Order verification of assets',
    category: 'Actions',
    icon: 'ti-wallet'
  },
  orderVOIEButton: {
    name: 'Order VOIE',
    description: 'Order verification of income/employment',
    category: 'Actions',
    icon: 'ti-briefcase'
  },
  runPricingButton: {
    name: 'Run Pricing',
    description: 'Run pricing scenarios and rate quotes',
    category: 'Actions',
    icon: 'ti-calculator'
  },
  runAUSButton: {
    name: 'Run AUS',
    description: 'Submit to automated underwriting system',
    category: 'Actions',
    icon: 'ti-robot'
  },
  pullFeesButton: {
    name: 'Pull Fees',
    description: 'Generate fees worksheet and closing costs',
    category: 'Actions',
    icon: 'ti-receipt'
  },
  
  // Advanced Features
  aiAssistant: {
    name: 'AI Assistant',
    description: 'Floating AI assistant for loan guidance',
    category: 'Advanced',
    icon: 'ti-robot'
  },
  bulkActions: {
    name: 'Bulk Actions',
    description: 'Bulk operations on multiple loans',
    category: 'Advanced',
    icon: 'ti-checkbox'
  },
  advancedReporting: {
    name: 'Advanced Reporting',
    description: 'Advanced analytics and reporting',
    category: 'Advanced',
    icon: 'ti-chart-bar'
  },
};

// Get all feature flags from localStorage
export const getFeatureFlags = () => {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_KEY);
    if (stored) {
      return { ...DEFAULT_FEATURE_FLAGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading feature flags:', error);
  }
  return DEFAULT_FEATURE_FLAGS;
};

// Save feature flags to localStorage
export const saveFeatureFlags = (flags) => {
  try {
    localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(flags));
    return true;
  } catch (error) {
    console.error('Error saving feature flags:', error);
    return false;
  }
};

// Check if a specific feature is enabled
export const isFeatureEnabled = (featureKey) => {
  const flags = getFeatureFlags();
  return flags[featureKey] === true;
};

// Toggle a specific feature
export const toggleFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = !flags[featureKey];
  saveFeatureFlags(flags);
  return flags[featureKey];
};

// Enable a specific feature
export const enableFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = true;
  saveFeatureFlags(flags);
};

// Disable a specific feature
export const disableFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = false;
  saveFeatureFlags(flags);
};

// Reset all flags to default
export const resetFeatureFlags = () => {
  saveFeatureFlags(DEFAULT_FEATURE_FLAGS);
  return DEFAULT_FEATURE_FLAGS;
};

// Get features grouped by category
export const getFeaturesByCategory = () => {
  const flags = getFeatureFlags();
  const grouped = {};
  
  Object.keys(FEATURE_METADATA).forEach(key => {
    const metadata = FEATURE_METADATA[key];
    const category = metadata.category;
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    
    grouped[category].push({
      key,
      ...metadata,
      enabled: flags[key]
    });
  });
  
  return grouped;
};

// Export default for easy import
export default {
  getFeatureFlags,
  saveFeatureFlags,
  isFeatureEnabled,
  toggleFeature,
  enableFeature,
  disableFeature,
  resetFeatureFlags,
  getFeaturesByCategory,
  FEATURE_METADATA
};
